import { escapeRegExp } from 'lodash';

import type { DataStreamModel, LogSourceModel } from './LogSourcesCreate.types';

/**
 * Merges existing backend sources with new user selections while preserving
 * sources that weren't explicitly deselected.
 *
 * @param existingSources - Current sources from backend (may include wildcards like 'product*')
 * @param newSources - Sources from current form submission
 * @param logSources - Full UI state including selection tracking metadata
 * @returns Merged array of source names
 *
 * Logic:
 * 1. Include all sources from new payload (user's current selections)
 * 2. Track explicitly user-deselected items (both parent and streams)
 * 3. Preserve backend sources that were neither selected nor deselected
 */
export const mergeSources = (
  existingSources: string[],
  newSources: string[],
  logSources: LogSourceModel[]
) => {
  const normalize = (val: string) => val?.toLowerCase().trim();
  const resultSet = new Set<string>();

  // STEP 1: Add all sources from new payload (already correct from generatePayloadFromSelectedItems)
  newSources.forEach((source) => {
    resultSet.add(normalize(source));
  });

  // STEP 2: Build a set of explicitly user-deselected sources
  const userDeselectedSet = new Set<string>();

  logSources.forEach((item) => {
    const normalizedName = normalize(item.name);

    // Check if parent was explicitly deselected
    if (item.selectionSource === 'user-deselected') {
      userDeselectedSet.add(normalizedName);
      userDeselectedSet.add(`${normalizedName}*`);
    }

    // Check for explicitly deselected streams
    item.data_streams?.forEach((stream: DataStreamModel) => {
      if (stream.selectionSource === 'user-deselected') {
        userDeselectedSet.add(normalize(stream.name));
      }
    });
  });

  // STEP 3: Preserve backend sources that were NOT explicitly deselected
  existingSources.forEach((src) => {
    const normalizedSrc = normalize(src);
    const baseName = normalizedSrc.replace('*', '');

    // Skip if already in new payload
    if (resultSet.has(normalizedSrc)) {
      return;
    }

    // Skip if explicitly deselected by user
    if (
      userDeselectedSet.has(normalizedSrc) ||
      userDeselectedSet.has(baseName) ||
      userDeselectedSet.has(`${baseName}*`)
    ) {
      return;
    }

    // Check if this source exists in the UI
    const existsInUI = logSources.some((item) => {
      const itemName = normalize(item.name);

      // Check if parent matches
      if (itemName === baseName) {
        return true;
      }

      // Check if any stream matches
      return (
        item.data_streams?.some(
          (stream) => normalize(stream.name) === normalizedSrc
        ) ?? false
      );
    });

    // Preserve values if:
    // - They are not displayed in the UI, or
    // - They are displayed but remain untouched by the user
    if (!existsInUI) {
      resultSet.add(normalizedSrc);
    } else {
      // Source is in UI but not in newSources and not user-deselected
      // This means it was untouched - preserve it
      const uiItem = logSources.find((item) => {
        const itemName = normalize(item.name);
        return (
          itemName === baseName ||
          item.data_streams?.some(
            (stream) => normalize(stream.name) === normalizedSrc
          )
        );
      });

      // Only preserve if it has 'unchanged' or 'auto-populated' status
      if (
        uiItem &&
        (uiItem.selectionSource === 'unchanged' ||
          uiItem.selectionSource === 'auto-populated')
      ) {
        resultSet.add(normalizedSrc);
      }
    }
  });

  return Array.from(resultSet);
};

// Utility function for wildcard matching
export const wildcardMatch = (pattern: string, name: string): boolean => {
  const escapedPattern = escapeRegExp(pattern).replace('\\*', '.*');
  const regex = new RegExp(`^${escapedPattern}$`);
  return regex.test(name);
};

// Utility function for exact matching (case-insensitive)
export const exactMatch = (pattern: string, name: string): boolean => {
  const escapedPattern = escapeRegExp(pattern);
  const regex = new RegExp(`^${escapedPattern}$`, 'i');
  return regex.test(name);
};
